import React, { useEffect, useState } from "react";
import { api, formatDate } from "../../lib/api";
import { Mail, Reply, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function MessagesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/messages");
      setList(data);
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async (m) => {
    if (m.is_read) return;
    try {
      await api.put(`/admin/messages/${m.id}/read`);
      setList((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_read: true } : x)));
    } catch (err) {
      // Non-fatal: marking-as-read is a passive convenience, not a critical write.
      console.warn("markRead failed", err);
    }
  };

  const remove = async (m, e) => {
    e.stopPropagation();
    if (!confirm(`Are you sure you want to delete this message from ${m.name}?\n\nSubject: "${m.subject}"\n\nIt will move to Trash and auto-delete after 30 days.`)) return;
    try {
      await api.delete(`/admin/messages/${m.id}`);
      setList((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Moved to Trash");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="messages-tab">
      <header className="mb-8">
        <p className="font-script text-2xl" style={{ color: "var(--text-accent)" }}>
          from the inbox
        </p>
        <h1 className="font-display text-4xl" style={{ color: "var(--text-primary)" }}>
          Messages
        </h1>
      </header>

      {loading ? (
        <p className="font-script text-xl" style={{ color: "var(--text-secondary)" }}>
          loading…
        </p>
      ) : list.length === 0 ? (
        <p style={{ color: "var(--text-secondary)" }}>No messages yet.</p>
      ) : (
        <div className="space-y-3">
          {list.map((m) => (
            <article
              key={m.id}
              onClick={() => markRead(m)}
              className="bg-white rounded-2xl p-5 cursor-pointer"
              style={{
                border: "1px solid var(--border-soft)",
                opacity: m.is_read ? 0.85 : 1,
              }}
              data-testid={`message-${m.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  {!m.is_read && (
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: "var(--brand-orange)" }}
                    />
                  )}
                  <Mail size={16} style={{ color: "var(--brand-sage)" }} />
                  <h3 className="font-display text-lg" style={{ color: "var(--text-primary)" }}>
                    {m.subject}
                  </h3>
                </div>
                <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  {formatDate(m.created_at)}
                </span>
              </div>
              <p className="text-sm mt-3 whitespace-pre-wrap break-words" style={{ color: "var(--text-primary)", overflowWrap: "anywhere" }}>
                {m.message}
              </p>
              <div
                className="flex items-center justify-between mt-4 pt-3 flex-wrap gap-2"
                style={{ borderTop: "1px solid var(--border-soft)" }}
              >
                <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
                  <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>{m.name}</span> · {m.email}
                </div>
                <a
                  href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold"
                  style={{ border: "1px solid var(--border-soft)", color: "var(--brand-sage)" }}
                  data-testid={`reply-message-${m.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Reply size={12} />
                  Reply
                </a>
                <button
                  onClick={(e) => remove(m, e)}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold"
                  style={{ border: "1px solid var(--border-soft)", color: "var(--brand-orange)" }}
                  data-testid={`delete-message-${m.id}`}
                >
                  <Trash2 size={12} />
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
